import { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../components/Layout/AdminLayout';
import { api } from '../../services/api';

const YEAR_OPTIONS = [2027, 2026, 2025];

type SubRow = {
  id: string;
  subcritId: string;
  label: string;
  maxScore: number;
  sortOrder: number;
  draft: string;
  saving: boolean;
  saved: boolean;
};

type CriterionRow = {
  id: string;
  criterionId: string;
  label: string;
  maxScore: number;
  sortOrder: number;
  rubricType: string;
  subcriteria: SubRow[];
  // draft edit state for parent max score
  draft: string;
  saving: boolean;
  saved: boolean;
  expanded: boolean;
};

export default function AdminReviewCriteriaPage() {
  const [selectedYear, setSelectedYear] = useState<number>(2027);
  const [rows, setRows]       = useState<CriterionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  const load = useCallback(async (year: number) => {
    setLoading(true);
    setError('');
    try {
      const data = await api.reviewCriteria.list(year);
      setRows(
        data.criteria.map((c) => ({
          ...c,
          draft:      String(c.maxScore),
          saving:     false,
          saved:      false,
          expanded:   false,
          subcriteria: c.subcriteria.map((s) => ({
            ...s,
            draft:  String(s.maxScore),
            saving: false,
            saved:  false,
          })),
        })),
      );
    } catch {
      setError('Nem sikerült betölteni a bírálati szempontokat.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(selectedYear); }, [load, selectedYear]);

  // Save parent criterion max score
  async function saveCriterion(idx: number) {
    const row = rows[idx];
    const parsed = parseInt(row.draft, 10);
    if (isNaN(parsed) || parsed < 0) return;

    setRows((prev) => prev.map((r, i) => i === idx ? { ...r, saving: true, saved: false } : r));
    try {
      await api.reviewCriteria.update(row.id, { maxScore: parsed });
      setRows((prev) => prev.map((r, i) =>
        i === idx ? { ...r, maxScore: parsed, saving: false, saved: true } : r,
      ));
    } catch {
      setRows((prev) => prev.map((r, i) => i === idx ? { ...r, saving: false } : r));
    }
  }

  // Save individual sub-criterion max score
  async function saveSubcriteria(critIdx: number, subIdx: number) {
    const sub = rows[critIdx].subcriteria[subIdx];
    const parsed = parseInt(sub.draft, 10);
    if (isNaN(parsed) || parsed < 0) return;

    setRows((prev) => prev.map((r, i) => {
      if (i !== critIdx) return r;
      return {
        ...r,
        subcriteria: r.subcriteria.map((s, j) =>
          j === subIdx ? { ...s, saving: true, saved: false } : s,
        ),
      };
    }));

    try {
      await api.reviewCriteria.updateSubcriteria(sub.id, { maxScore: parsed });
      setRows((prev) => prev.map((r, i) => {
        if (i !== critIdx) return r;
        return {
          ...r,
          subcriteria: r.subcriteria.map((s, j) =>
            j === subIdx ? { ...s, maxScore: parsed, saving: false, saved: true } : s,
          ),
        };
      }));
    } catch {
      setRows((prev) => prev.map((r, i) => {
        if (i !== critIdx) return r;
        return {
          ...r,
          subcriteria: r.subcriteria.map((s, j) =>
            j === subIdx ? { ...s, saving: false } : s,
          ),
        };
      }));
    }
  }

  const baseTotal  = rows.filter((r) => !r.criterionId.startsWith('bonus')).reduce((s, r) => s + (parseInt(r.draft, 10) || 0), 0);
  const bonusTotal = rows.filter((r) =>  r.criterionId.startsWith('bonus')).reduce((s, r) => s + (parseInt(r.draft, 10) || 0), 0);

  const baseRows  = rows.filter((r) => !r.criterionId.startsWith('bonus'));
  const bonusRows = rows.filter((r) =>  r.criterionId.startsWith('bonus'));

  const SectionTable = ({ title, items, offset }: { title: string; items: CriterionRow[]; offset: number }) => (
    <div style={{ marginBottom: 28 }}>
      <div style={{ fontSize: 15, fontWeight: 700, color: '#1f2937', marginBottom: 10, paddingBottom: 6, borderBottom: '1px solid #e5e7eb' }}>
        {title}
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ background: '#f9fafb' }}>
            <th style={{ padding: '8px 12px', textAlign: 'left',   fontWeight: 700, color: '#374151', border: '1px solid #e5e7eb', width: 60 }}>ID</th>
            <th style={{ padding: '8px 12px', textAlign: 'left',   fontWeight: 700, color: '#374151', border: '1px solid #e5e7eb' }}>Megnevezés</th>
            <th style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 700, color: '#374151', border: '1px solid #e5e7eb', width: 80 }}>Típus</th>
            <th style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 700, color: '#374151', border: '1px solid #e5e7eb', width: 120 }}>Max pont</th>
            <th style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 700, color: '#374151', border: '1px solid #e5e7eb', width: 80 }}>Státusz</th>
          </tr>
        </thead>
        <tbody>
          {items.map((row, i) => {
            const idx      = offset + i;
            const isDirty  = row.draft !== String(row.maxScore);
            const subTotal = row.subcriteria.reduce((s, sub) => s + (parseInt(sub.draft, 10) || 0), 0);
            const subMismatch = row.rubricType === 'standard' && subTotal !== (parseInt(row.draft, 10) || 0) && subTotal > 0;

            return (
              <>
                <tr key={row.id} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                  <td style={{ padding: '8px 12px', border: '1px solid #e5e7eb', fontFamily: 'monospace', color: '#6b7280' }}>
                    {row.criterionId}
                  </td>
                  <td style={{ padding: '8px 12px', border: '1px solid #e5e7eb', color: '#111827' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {row.label}
                      {row.rubricType === 'standard' && (
                        <button
                          onClick={() => setRows((prev) => prev.map((r, j) => j === idx ? { ...r, expanded: !r.expanded } : r))}
                          style={{
                            fontSize: 11, padding: '1px 7px', borderRadius: 4,
                            border: '1px solid #d1d5db', background: row.expanded ? '#dbeafe' : '#f3f4f6',
                            color: row.expanded ? '#1e40af' : '#6b7280',
                            cursor: 'pointer', fontWeight: 600,
                          }}
                        >
                          {row.expanded ? '▲ részszempontok' : '▼ részszempontok'}
                        </button>
                      )}
                      {subMismatch && (
                        <span style={{ fontSize: 11, color: '#b45309', fontWeight: 600 }}>
                          ⚠ részszempontok összege: {subTotal}
                        </span>
                      )}
                    </div>
                  </td>
                  <td style={{ padding: '8px 12px', border: '1px solid #e5e7eb', textAlign: 'center' }}>
                    <span style={{
                      fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999,
                      background: row.rubricType === 'standard' ? '#dbeafe' : '#fef3c7',
                      color:      row.rubricType === 'standard' ? '#1e40af' : '#92400e',
                    }}>
                      {row.rubricType === 'standard' ? '4 részszempont' : 'egyszerű'}
                    </span>
                  </td>
                  <td style={{ padding: '6px 12px', border: '1px solid #e5e7eb', textAlign: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'center' }}>
                      <input
                        type="number" min={0} max={999}
                        value={row.draft}
                        onChange={(e) => {
                          const v = e.target.value;
                          setRows((prev) => prev.map((r, j) => j === idx ? { ...r, draft: v, saved: false } : r));
                        }}
                        onBlur={() => { if (isDirty) void saveCriterion(idx); }}
                        onKeyDown={(e) => { if (e.key === 'Enter') void saveCriterion(idx); }}
                        style={{
                          width: 64, padding: '4px 8px', textAlign: 'center',
                          borderRadius: 6,
                          border: '2px solid ' + (isDirty ? '#f59e0b' : '#d1d5db'),
                          fontSize: 14, fontWeight: 700, color: '#111827',
                        }}
                      />
                      <span style={{ fontSize: 11, color: '#9ca3af' }}>pt</span>
                    </div>
                  </td>
                  <td style={{ padding: '8px 12px', border: '1px solid #e5e7eb', textAlign: 'center', fontSize: 11 }}>
                    {row.saving && <span style={{ color: '#7a5a00' }}>Mentés…</span>}
                    {row.saved && !row.saving && <span style={{ color: '#15803d' }}>✓ Mentve</span>}
                    {isDirty && !row.saving && !row.saved && <span style={{ color: '#b45309' }}>Módosítva</span>}
                  </td>
                </tr>

                {/* Sub-criteria rows (expanded) */}
                {row.expanded && row.subcriteria.map((sub, si) => {
                  const subDirty = sub.draft !== String(sub.maxScore);
                  return (
                    <tr key={sub.id} style={{ background: '#f0f9ff' }}>
                      <td style={{ padding: '6px 12px 6px 28px', border: '1px solid #e5e7eb', fontFamily: 'monospace', color: '#94a3b8', fontSize: 11 }}>
                        └ {sub.subcritId}
                      </td>
                      <td style={{ padding: '6px 12px 6px 16px', border: '1px solid #e5e7eb', color: '#374151', fontSize: 12 }}>
                        {sub.label}
                      </td>
                      <td style={{ padding: '6px 12px', border: '1px solid #e5e7eb', textAlign: 'center' }}>
                        <span style={{ fontSize: 11, color: '#64748b' }}>al-szempont</span>
                      </td>
                      <td style={{ padding: '5px 12px', border: '1px solid #e5e7eb', textAlign: 'center' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'center' }}>
                          <input
                            type="number" min={0} max={999}
                            value={sub.draft}
                            onChange={(e) => {
                              const v = e.target.value;
                              setRows((prev) => prev.map((r, j) => {
                                if (j !== idx) return r;
                                return {
                                  ...r,
                                  subcriteria: r.subcriteria.map((s, k) =>
                                    k === si ? { ...s, draft: v, saved: false } : s,
                                  ),
                                };
                              }));
                            }}
                            onBlur={() => { if (subDirty) void saveSubcriteria(idx, si); }}
                            onKeyDown={(e) => { if (e.key === 'Enter') void saveSubcriteria(idx, si); }}
                            style={{
                              width: 64, padding: '3px 8px', textAlign: 'center',
                              borderRadius: 6,
                              border: '2px solid ' + (subDirty ? '#f59e0b' : '#bfdbfe'),
                              fontSize: 13, fontWeight: 700, color: '#1e40af',
                            }}
                          />
                          <span style={{ fontSize: 11, color: '#9ca3af' }}>pt</span>
                        </div>
                      </td>
                      <td style={{ padding: '6px 12px', border: '1px solid #e5e7eb', textAlign: 'center', fontSize: 11 }}>
                        {sub.saving && <span style={{ color: '#7a5a00' }}>Mentés…</span>}
                        {sub.saved && !sub.saving && <span style={{ color: '#15803d' }}>✓</span>}
                        {subDirty && !sub.saving && !sub.saved && <span style={{ color: '#b45309' }}>●</span>}
                      </td>
                    </tr>
                  );
                })}
              </>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  return (
    <AdminLayout>
      <div style={{ padding: 28, maxWidth: 860 }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Bírálati szempontok</h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 13, color: '#6b7280', fontWeight: 600 }}>Év:</span>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
              style={{ padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }}
            >
              {YEAR_OPTIONS.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        </div>

        {/* Instructions */}
        <div style={{ padding: '10px 14px', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8, marginBottom: 20, fontSize: 13, color: '#0369a1' }}>
          A fő szemponthoz beírt pontszám az összesítőben szerepel. Rubric típusú szempontoknál a <strong>▼ részszempontok</strong> gombbal állíthatod be egyenként a 4 al-szempont max pontját. Az összegük egyezzen meg a fő max ponttal (eltérés esetén figyelmeztetés jelenik meg).
        </div>

        {error && (
          <div style={{ marginBottom: 16, padding: '10px 14px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 6, color: '#dc2626', fontSize: 13 }}>
            {error}
          </div>
        )}

        {loading ? (
          <div style={{ color: '#9ca3af', fontSize: 14 }}>Betöltés…</div>
        ) : rows.length === 0 ? (
          <div style={{ color: '#9ca3af', fontSize: 14 }}>Nincs szempont a(z) {selectedYear}. évre.</div>
        ) : (
          <>
            <SectionTable title="Fő értékelési szempontok" items={baseRows}  offset={0} />
            <SectionTable title="Bónusz szempontok"        items={bonusRows} offset={baseRows.length} />

            {/* Totals */}
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginTop: 8 }}>
              <div style={{
                flex: 1, padding: '12px 18px', borderRadius: 8,
                background: baseTotal === 150 ? '#f0fdf4' : '#fffbeb',
                border: '1px solid ' + (baseTotal === 150 ? '#86efac' : '#fde68a'),
              }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 2 }}>Fő szempontok összege</div>
                <div style={{ fontSize: 24, fontWeight: 800, color: baseTotal === 150 ? '#15803d' : '#b45309' }}>
                  {baseTotal} <span style={{ fontSize: 13, fontWeight: 400, color: '#6b7280' }}>/ 150</span>
                </div>
                {baseTotal !== 150 && (
                  <div style={{ fontSize: 11, color: '#b45309', marginTop: 2 }}>
                    A javasolt összeg 150 pont. Különbség: {baseTotal > 150 ? '+' : ''}{baseTotal - 150}
                  </div>
                )}
              </div>
              <div style={{
                flex: 1, padding: '12px 18px', borderRadius: 8,
                background: bonusTotal <= 10 ? '#f0fdf4' : '#fef2f2',
                border: '1px solid ' + (bonusTotal <= 10 ? '#86efac' : '#fca5a5'),
              }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', marginBottom: 2 }}>Bónusz összege</div>
                <div style={{ fontSize: 24, fontWeight: 800, color: bonusTotal <= 10 ? '#15803d' : '#dc2626' }}>
                  {bonusTotal} <span style={{ fontSize: 13, fontWeight: 400, color: '#6b7280' }}>/ 10 (max)</span>
                </div>
                {bonusTotal > 10 && (
                  <div style={{ fontSize: 11, color: '#dc2626', marginTop: 2 }}>
                    A bónusz összege meghaladja a 10 pontos maximumot!
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
